import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/api";

const AuthContext = createContext(null);

export const ROLE_HOME = {
  employee: "/app",
  employer_admin: "/employer",
  advisor: "/advisor",
  platform_admin: "/admin",
};

// A public consumer is an `employee` with no employer link (worksite employees always have one).
// Consumers land in the Nauticus member experience; everyone else uses their role home.
export function isConsumerAccount(user) {
  return user?.role === "employee" && !user?.employer_id;
}
export function homeForUser(user) {
  if (!user) return "/login";
  if (isConsumerAccount(user)) return "/app/member";
  return ROLE_HOME[user.role] || "/app";
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = anon, object = user
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data.user);
    } catch {
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = async (email, password, extra = {}) => {
    const { data } = await api.post("/auth/login", { email, password, ...extra });
    setUser(data.user);
    return data.user;
  };

  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    setUser(data.user);
    return data.user;
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {}
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, refresh, setUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
